import { UseIncidentsPageResultIProps } from '../hooks/useIncidentsPage'
import { useTranslation } from 'react-i18next'

import { Box, Circle, Flex, Skeleton } from '@chakra-ui/react'

import { ActionButtons } from '@/components/layouts/ActionButtons'
import { EditModeFrame } from '@/components/layouts/EditMode'
import { Header } from '@/components/layouts/Header'

import { IncidentsSearchFilterHeader } from './IncidentsSearchFilterHeader'
import { IncidentsTable } from './IncidentsTable'

/**
 * The Incidents page in its current (pre-rebrand) styling. Rendered when
 * `FeatureFlag.incidentDrawerRebrand` is off. Extracted verbatim from the page
 * during the whole-page rebrand (ENG-30677) so the dark and legacy skins share
 * one data source (`useIncidentsPage`).
 */
export const LegacyIncidentsPage = ({
  incidentList,
  totalIncidents,
  hasLoadedOnce,
  isTableLoading,
  paginationState,
  setPaginationState,
  sorting,
  setSorting,
  rowSelection,
  setRowSelection,
  queryFilter,
  setQueryFilter,
  isPriorityQueueActive,
  incidentsView,
  onSelectView,
}: UseIncidentsPageResultIProps) => {
  const { t } = useTranslation()
  const shouldShowTable = isTableLoading || incidentList.length > 0

  return (
    <Box>
      <Header
        dataTestId='incidentsPage_header'
        left={
          <Flex alignItems='center' paddingLeft='6'>
            <Box fontSize='20px' fontWeight='extrabold'>
              {t('incidents:page_title')}
            </Box>
            {hasLoadedOnce ? (
              <Circle
                bg='#14327A'
                color='white'
                data-testid='incidentsPage_header_incidentsCount'
                fontSize='16px'
                fontWeight='bold'
                h='24px'
                ml='3'
                paddingX='9px'
              >
                {totalIncidents}
              </Circle>
            ) : (
              <Skeleton h='24px' ml='3' rounded='full' w='24px' />
            )}
          </Flex>
        }
        right={<ActionButtons />}
      />
      <Box h='calc(100% - 65px)' mx='auto' pos='relative' w='full'>
        <EditModeFrame />
        <IncidentsSearchFilterHeader
          filter={queryFilter}
          incidentsView={incidentsView}
          isPriorityQueueActive={isPriorityQueueActive}
          onSelectView={onSelectView}
          setFilter={setQueryFilter}
        />
        <Box overflow='hidden' pos='relative'>
          <Box
            h={{ base: 'calc(100vh - 194px)', md: 'calc(100vh - 138px)' }}
            overflow='scroll'
            overflowX='auto'
            p={{ md: 0, lg: 6 }}
            textAlign='center'
            transition='ease-in-out'
            transitionDuration='300ms'
            transitionProperty='all'
            w='full'
          >
            {shouldShowTable ? (
              <IncidentsTable
                incidents={incidentList}
                isLoading={isTableLoading}
                isPriorityQueueActive={isPriorityQueueActive}
                paginationState={paginationState}
                rowSelection={rowSelection}
                setPaginationState={setPaginationState}
                setRowSelection={setRowSelection}
                setSorting={setSorting}
                sorting={sorting}
                totalIncidents={totalIncidents}
              />
            ) : (
              <Box textAlign='center'>{t('common:list_empty')}</Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
